import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Pause, Play, Trash2, PackageOpen, Plus } from "lucide-react";
import { SubPage, EmptyState, Spinner } from "../components/common";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { api, fileUrl } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAppData } from "../context/AppDataContext";
import { formatPrice, relativeDate } from "../lib/format";

function ActionBtn({ onClick, icon: Icon, label, danger, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex flex-1 items-center justify-center gap-1.5 rounded-full border px-2 py-2 text-xs font-bold transition-colors duration-200 ${
        danger ? "border-alert/30 text-alert hover:bg-alert/10" : "border-slate-200 text-navy hover:bg-mist"}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

export default function MyListingsPage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { catName, zoneName } = useAppData();
  const [items, setItems] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => { api.get("/me/listings").then((r) => setItems(r.data)); }, []);

  const setStatus = async (l, status) => {
    const { data } = await api.post(`/listings/${l.id}/status`, { status });
    setItems((xs) => xs.map((x) => (x.id === l.id ? data : x)));
    toast.success(t(status === "paused" ? "mine.pausedToast" : "mine.reactivatedToast"));
  };
  const remove = async () => {
    await api.delete(`/listings/${toDelete.id}`);
    setItems((xs) => xs.filter((x) => x.id !== toDelete.id));
    setToDelete(null);
    toast.success(t("mine.deleted"));
  };

  const publishBtn = (
    <button onClick={() => navigate("/publicar")} className="btn-navy px-4 py-2 text-sm" data-testid="mylistings-publish-btn">
      <Plus className="h-4 w-4" /> {t("profile.publish")}
    </button>
  );

  return (
    <SubPage title={t("mine.title")} testid="mylistings-page" right={items?.length ? publishBtn : null}>
      {!items ? <div className="flex justify-center py-10"><Spinner /></div> : items.length === 0 ? (
        <EmptyState icon={PackageOpen} title={t("mine.empty")} sub={t("mine.emptySub")} action={publishBtn} testid="mylistings-empty" />
      ) : (
        <div className="space-y-3">
          {items.map((l) => (
            <div key={l.id} className="rounded-2xl bg-white p-3 shadow-[0_2px_12px_rgba(13,42,107,0.07)] fade-up" data-testid={`mylisting-${l.id}`}>
              <Link to={`/anuncio/${l.id}`} className="flex gap-3">
                <img src={fileUrl(l.photos[0])} alt="" className={`h-20 w-20 shrink-0 rounded-xl object-cover ${l.status === "paused" ? "opacity-50 grayscale" : ""}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="line-clamp-2 font-bold leading-tight text-navy">{l.title}</p>
                    <span data-testid={`mylisting-status-${l.id}`}
                      className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${l.status === "active" ? "bg-brand/15 text-brand" : "bg-slate-200 text-slate-600"}`}>
                      {t(l.status === "active" ? "mine.active" : "mine.paused")}
                    </span>
                  </div>
                  <p className="mt-0.5 font-extrabold text-navy">{formatPrice(l, t, lang)}</p>
                  <p className="truncate text-xs text-slate-500">{catName(l.category)} · {zoneName(l.zone)} · {relativeDate(l.created_at, lang)}</p>
                </div>
              </Link>
              <div className="mt-3 flex gap-2">
                <ActionBtn icon={Pencil} label={t("mine.edit")} onClick={() => navigate(`/editar/${l.id}`)} testid={`mylisting-edit-${l.id}`} />
                {l.status === "active" ? (
                  <ActionBtn icon={Pause} label={t("mine.pause")} onClick={() => setStatus(l, "paused")} testid={`mylisting-pause-${l.id}`} />
                ) : (
                  <ActionBtn icon={Play} label={t("mine.reactivate")} onClick={() => setStatus(l, "active")} testid={`mylisting-reactivate-${l.id}`} />
                )}
                <ActionBtn icon={Trash2} label={t("mine.delete")} danger onClick={() => setToDelete(l)} testid={`mylisting-delete-${l.id}`} />
              </div>
            </div>
          ))}
        </div>
      )}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="w-[calc(100%-2rem)] max-w-md rounded-3xl" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-navy">{t("mine.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("mine.deleteSub")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded-full" data-testid="delete-cancel-btn">{t("mine.cancel")}</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-alert hover:bg-alert/90" onClick={remove} data-testid="delete-confirm-btn">{t("mine.confirmDelete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SubPage>
  );
}
